import DOMPurify from "dompurify";
import { getBaseUrl } from "@/services/api/baseUrl";

/**
 * The sanitiser policy for comment bodies.
 *
 * A comment is written by one user in a rich-text editor and rendered into
 * another user's review screen, so it is untrusted input that happens to arrive
 * as HTML. DOMPurify strips scripting on its own; this module exists for the
 * part it deliberately leaves alone once `style` and `class` are allowed:
 *
 *   - Inline CSS can *place* an element. `position: fixed` on a comment is an
 *     invisible sheet over Approve and Reject, drawn by anyone who can leave a
 *     note on the invoice. Only the properties the toolbar actually emits live.
 *   - A class name is a handle on the app's stylesheet. The Syncfusion theme is
 *     loaded globally by the editor and the PDF viewer, so `class="e-popup"` is
 *     `position: absolute` for free. Only the image-layout classes the editor
 *     writes itself live.
 *   - A remote `<img>` is not a picture, it is a read receipt: it reports the
 *     reviewer's IP and user agent to whoever chose the URL. The editor inlines
 *     inserted images as data URLs, so nothing legitimate points off-origin.
 *
 * This is a private DOMPurify instance rather than the shared default export.
 * Hooks are registered per instance, and these are the rules for comment HTML
 * specifically — not for anything else that might one day call `sanitize()`.
 */
const purify = DOMPurify(window);

/**
 * Wide enough to round-trip what the rich-text comment editor emits: `font` and
 * styled `span`s for the colour/size controls, `sub`/`sup` and `hr` from
 * Formats, sizing attributes on pasted images.
 */
const ALLOWED_TAGS = [
  "p", "br", "hr", "strong", "b", "em", "i", "u", "s", "span", "div", "font",
  "sub", "sup", "ul", "ol", "li", "a", "code", "pre", "blockquote",
  "h1", "h2", "h3", "h4", "h5", "h6", "img", "figure", "figcaption",
];

/**
 * `style` and `class` are allowed here and then narrowed in the hook below —
 * DOMPurify drops an attribute that is not on this list before any hook can see
 * it, so filtering their *values* means first letting them through.
 */
const ALLOWED_ATTR = [
  "href", "title", "target", "rel", "src", "alt", "style", "class",
  "width", "height", "color", "face", "size",
];

/**
 * The CSS the toolbar can produce: FontColor, BackgroundColor, FontName,
 * FontSize, the Formats and Alignments dropdowns, LowerCase/UpperCase, and
 * `sub`/`sup`. Longhands are listed because the browser expands shorthands like
 * `text-decoration` before we get to read the declaration back.
 *
 * Everything absent is the point of the list: no `position`, `top`, `left`,
 * `z-index`, `transform`, `opacity` or `pointer-events`, which are what turn a
 * styled comment into an overlay on the buttons underneath it.
 */
const ALLOWED_STYLE_PROPERTIES = new Set([
  "color",
  "background-color",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "text-align",
  "text-decoration",
  "text-decoration-color",
  "text-decoration-line",
  "text-decoration-style",
  "text-decoration-thickness",
  "text-transform",
  "vertical-align",
  "list-style-type",
]);

/** Sizing is meaningful on a resized image and nowhere else a comment can reach. */
const IMAGE_ONLY_STYLE_PROPERTIES = new Set(["width", "height"]);

/**
 * The classes Syncfusion writes onto an inserted image for alignment, inline
 * flow and captions. Named individually rather than matched on the `e-` prefix,
 * because that prefix also covers the theme's positioned classes.
 */
const ALLOWED_CLASSES = new Set([
  "e-rte-image",
  "e-imginline",
  "e-imgbreak",
  "e-imgleft",
  "e-imgcenter",
  "e-imgright",
  "e-img-caption",
  "e-imgcaption",
  "e-caption-inline",
]);

/** Raster data URLs only — not `svg+xml`, which carries markup of its own. */
const DATA_IMAGE_PATTERN = /^data:image\/(png|jpeg|jpg|gif|webp|bmp);base64,/i;

/**
 * The app's own origin, plus the API origin it uploads attachments to — the
 * same host in a deployed environment, a separate one on localhost.
 */
function allowedImageOrigins() {
  const origins = new Set([window.location.origin]);
  try {
    origins.add(new URL(getBaseUrl(), window.location.href).origin);
  } catch {
    /* malformed base URL — the page origin alone is the allowlist */
  }
  return origins;
}

function isAllowedImageSrc(value) {
  const src = value?.trim();
  if (!src) return false;
  if (DATA_IMAGE_PATTERN.test(src)) return true;

  try {
    const url = new URL(src, window.location.href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    return allowedImageOrigins().has(url.origin);
  } catch {
    return false;
  }
}

/** Drops every declaration outside the allowlist; removes the attribute if none survive. */
function filterStyle(node) {
  if (!node.hasAttribute("style")) return;

  const isImage = node.tagName === "IMG";
  // Snapshot the names first: removing a property mutates what we are iterating.
  const declared = Array.from(node.style);

  declared.forEach((property) => {
    const allowed =
      ALLOWED_STYLE_PROPERTIES.has(property) ||
      (isImage && IMAGE_ONLY_STYLE_PROPERTIES.has(property));
    if (!allowed) node.style.removeProperty(property);
  });

  if (!node.getAttribute("style")?.trim()) node.removeAttribute("style");
}

function filterClass(node) {
  if (!node.hasAttribute("class")) return;

  const kept = node
    .getAttribute("class")
    .split(/\s+/)
    .filter((name) => ALLOWED_CLASSES.has(name));

  if (kept.length) {
    node.setAttribute("class", kept.join(" "));
  } else {
    node.removeAttribute("class");
  }
}

purify.addHook("afterSanitizeAttributes", (node) => {
  // Text and comment nodes reach this hook too; only elements carry attributes.
  if (node.nodeType !== 1) return;

  // Force every surviving link to be safe to open.
  if (node.tagName === "A" && node.hasAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }

  // An off-origin image is removed outright rather than left as a broken icon.
  if (node.tagName === "IMG" && !isAllowedImageSrc(node.getAttribute("src"))) {
    node.remove();
    return;
  }

  filterStyle(node);
  filterClass(node);
});

/** @param {string} [html] the comment body as the API stored it. */
export function sanitizeCommentHtml(html) {
  return purify.sanitize(html ?? "", { ALLOWED_TAGS, ALLOWED_ATTR });
}
